import { el } from '../dom.js';
import type { I18n } from '../i18n.js';

/**
 * In-app Privacy Policy. Mirrors docs/privacy-notice.md — that file remains
 * the authoritative public text and must be kept in sync with these i18n keys.
 *
 * Intentionally does NOT repeat consent.sensitive_notice: that trigger warning
 * is shown in the landing spoiler, not here.
 */

export function PrivacyView({ i18n, onBack }: { i18n: I18n; onBack: () => void }): HTMLElement {
  const section = (headingKey: string, children: (HTMLElement | string)[]): HTMLElement =>
    el('section', { class: 'privacy-section' }, [
      el('h2', {}, i18n.t(headingKey)),
      ...children,
    ]);

  const p = (key: string): HTMLElement => el('p', {}, i18n.t(key));
  const li = (key: string): HTMLElement => el('li', {}, i18n.t(key));

  return el('article', { class: 'privacy-doc', 'aria-label': i18n.t('privacy.heading') }, [
    el('div', { class: 'privacy-hero' }, [
      el('h1', {}, i18n.t('privacy.heading')),
      el('p', { class: 'privacy-updated' }, i18n.t('privacy.last_updated', { date: '2026-05-10' })),
      el('p', { class: 'lede' }, i18n.t('privacy.intro')),
    ]),

    section('privacy.collect.heading', [
      el('ul', {}, [
        li('privacy.collect.session'),
        li('privacy.collect.responses'),
        li('privacy.collect.demographics'),
        li('privacy.collect.onboarding'),
        li('privacy.collect.technical'),
      ]),
    ]),

    section('privacy.analytics.heading', [
      p('privacy.analytics.intro'),
      el('ul', {}, [
        li('privacy.analytics.os'),
        li('privacy.analytics.browser'),
        li('privacy.analytics.timezone'),
        li('privacy.analytics.screen'),
        li('privacy.analytics.country'),
        li('privacy.analytics.timing'),
      ]),
      p('privacy.analytics.ip_note'),
    ]),

    section('privacy.not_collect.heading', [
      p('privacy.not_collect.body'),
    ]),

    section('privacy.purpose.heading', [
      p('privacy.purpose.body'),
    ]),

    section('privacy.storage.heading', [
      p('privacy.storage.body'),
    ]),

    section('privacy.access.heading', [
      p('privacy.access.body'),
    ]),

    section('privacy.rights.heading', [
      el('ul', {}, [
        li('privacy.rights.withdraw'),
        li('privacy.rights.delete'),
        li('privacy.rights.contact'),
      ]),
    ]),

    section('privacy.cookies.heading', [
      p('privacy.cookies.body'),
    ]),

    section('privacy.legal.heading', [
      el('ul', {}, [
        li('privacy.legal.art6'),
        li('privacy.legal.art9'),
        li('privacy.legal.art9j'),
      ]),
    ]),

    section('privacy.changes.heading', [
      p('privacy.changes.body'),
    ]),

    section('privacy.age.heading', [
      p('privacy.age.body'),
    ]),

    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-primary' }, [
        el('button', { class: 'secondary', onClick: () => onBack() }, i18n.t('privacy.back')),
      ]),
    ]),
  ]);
}
